import React, { useCallback, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { FaChevronUp, FaChevronDown } from 'react-icons/fa'
import { Link } from 'gatsby'
import { useNavigate } from '@reach/router'
import styled, { css } from 'styled-components'

import useCoursesPath from '../../hooks/useCoursesPath'
import icons from '../../utils/icons'
import courseTheme from '../../constants/theme'

const Lesson = styled.div`
  ${({ theme }) => css`
    align-items: center;
    border-bottom: 1px solid ${theme.colors.neutral.gray300};
    padding: calc(${courseTheme.gutter} / 2) 0;

    &:first-child {
      padding-top: 0;
    }
    display: grid;
    grid-template:
      'icon title preview time' max-content
      '. desc . .' max-content
      / max-content 1fr max-content max-content;
  `}
`

const IconLink = styled(Link).attrs({ rel: 'nofollow noopener noreferrer' })`
  grid-area: icon;
  padding-right: calc(${courseTheme.gutter} / 2);
`

const Title = styled.div`
  grid-area: title;
  display: flex;
  font-size: ${({ theme }) => theme.fontSizes.small};
`

const Preview = styled.button`
  ${({ theme }) => css`
    grid-area: preview;
    font-size: ${theme.fontSizes.xsmall};
    color: ${theme.colors.accent.primaryDarken15};
    background-color: ${theme.colors.neutral.white};
    padding: ${theme.spacers.xxsmall};
    margin-right: calc(${courseTheme.gutter} / 2);
    border: 1px solid ${theme.colors.accent.primaryDarken15};
    border-radius: 2rem;
    outline: none;
    cursor: pointer;
    transition: color 0.25s, background-color 0.25s, border 0.25s;

    &:hover {
      color: ${theme.colors.neutral.white};
      background-color: ${theme.colors.main.secondary};
      border: 1px solid ${theme.colors.main.secondary};
    }
  `}
`

const Desc = styled.p`
  ${({ theme }) => css`
    grid-area: desc;
    padding-left: calc(${courseTheme.gutter} / 2);
    font-size: ${theme.fontSizes.small};
    color: ${theme.colors.neutral.gray900};

    &.desc {
      &-enter {
        opacity: 0;
      }

      &-enter-active {
        opacity: 1;
        transition: opacity 200ms;
      }

      &-exit {
        opacity: 1;
      }

      &-exit-active {
        opacity: 0;
        transition: opacity 200ms;
      }
    }
  `}
`

const Time = styled.div`
  grid-area: time;
`

const ToggleDescIcon = styled.span`
  ${({ theme, courseType }) => css`
    display: flex;
    align-items: center;
    padding: 0 ${theme.spacers.xxsmall};
    margin: 0 ${theme.spacers.xxsmall};
    color: ${theme.colors.neutral.gray700};
    border-radius: 50%;
    cursor: pointer;
    transition: 0.25s background-color;

    ${courseType === 'enterprise' && `margin-left: auto`};

    &:hover {
      background-color: ${theme.colors.neutral.gray200};
    }
  `}
`

function LessonTypeIcon({ type }) {
  const Icon = icons[type]

  return <Icon></Icon>
}

export default function LessonItem({
  courseType,
  courseSlug,
  type,
  title,
  desc,
  time,
  slug,
  preview,
}) {
  const [isDescShown, setIsDescShown] = useState(false)
  const navigate = useNavigate()
  const { getLessonPath } = useCoursesPath()
  const lessonPath = getLessonPath(courseSlug, slug)

  const navigateToLesson = useCallback((slug) => () => navigate(lessonPath), [
    navigate,
    lessonPath,
  ])

  const toggleIsDescShown = useCallback(() => setIsDescShown(!isDescShown), [
    isDescShown,
    setIsDescShown,
  ])

  return (
    <Lesson>
      {courseType === 'online' && (
        <IconLink to={lessonPath}>
          <LessonTypeIcon type={type}></LessonTypeIcon>
        </IconLink>
      )}
      <Title>
        {title}
        <ToggleDescIcon courseType={courseType} onClick={toggleIsDescShown}>
          {isDescShown ? (
            <FaChevronUp></FaChevronUp>
          ) : (
            <FaChevronDown></FaChevronDown>
          )}
        </ToggleDescIcon>
      </Title>
      <CSSTransition
        in={isDescShown}
        timeout={200}
        classNames="desc"
        unmountOnExit
      >
        <Desc>{desc}</Desc>
      </CSSTransition>
      {courseType === 'online' && (
        <>
          {JSON.parse(preview) && (
            <Preview onClick={navigateToLesson(slug)}>ตัวอย่าง</Preview>
          )}
          <Time>{time}</Time>
        </>
      )}
    </Lesson>
  )
}
